/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { consoleCommands, events } from 'modules/common/ui-controller';
import { ROUTE_CLEAR } from 'modules/common/ui-controller/utils/command-router';
import { dashboardActions } from 'modules/dashboard/slice';
import { webClickActions } from 'modules/dashboard/components/tab-container/web-click-tab/slice';
import { iPreferActions } from 'modules/dashboard/components/tab-container/i-prefer-tab/slice';

// tab names of the tab list, grouped for the tabs that share the same setters
const SUMMARY = ['SUMMARY'];
const WORKBOOK = ['WORKBOOK_REPORT'];
const REGION_MAP = ['REGION_MAP'];
const TREND = ['TREND_TIME', 'PBI_TREND'];
const TREND_TABULAR = ['MUI_TREND_TIME'];
const PACE = ['PACE', 'PACE_TEST'];
// web click and i prefer tabs are dormant: they are not in TAB_CONFIG of the tab container, so their
// routes never match and their command pipelines never run until the tabs are enabled again
const WEB_CLICK = ['WEB_CLICK'];
const I_PREFER = ['I_PREFER'];
export const DORMANT_TABS = [...WEB_CLICK, ...I_PREFER];
// every tab
const ANY_TAB = null;

const isOn = (value) => value === 1;
/**
 * Where each generic command of a command url is forwarded, by tab.
 * A route is the UiController setter of the tab, or a function for the commands that set the
 * state directly. See getCommandRoute for a route entry and useCommandRouter for how it is used.
 * The tab steps that apply the forwarded values are in the *-command-steps.js file of each tab.
 */
const COMMAND_ROUTES = [
  { event: events.SET_GRANULARITY, routes: [[TREND_TABULAR, 'setTrendTabularGranularity']] },
  {
    event: events.SET_ROOM_NIGHTS_PACE,
    routes: [[WORKBOOK, 'setWorkbookRoomNightsPaceView']],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_ROOM_TYPE,
    routes: [[WORKBOOK, 'setWorkbookRoomType']],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_RRV,
    routes: [[TREND, 'setTrendRibbonRevenueView']],
  },
  {
    event: events.SET_ROV,
    routes: [[TREND, 'setTrendRibbonOccupiedRoomsView']],
  },
  {
    event: events.SET_RAV,
    routes: [[TREND, 'setTrendRibbonAdrView']],
  },
  {
    event: events.SET_DATE_TYPE,
    routes: [[WORKBOOK, 'setWorkbookDateType']],
  },
  {
    event: events.SET_RATE_PLAN,
    routes: [[WORKBOOK, 'setWorkbookRatePlan']],
  },
  {
    event: events.SET_IATA,
    routes: [[WORKBOOK, 'setWorkbookIATA']],
  },
  {
    // currency is not a tab filter, it is applied for every tab
    event: events.SET_CURRENCY,
    routes: [
      [
        ANY_TAB,
        (value, dispatch) => {
          dispatch(dashboardActions.setLocalCurrency(String(value).toUpperCase()));
          // kept for the tab of the url, it is applied again after the hotel commands of the tab
          dispatch(dashboardActions.setCommandRunValue({ key: consoleCommands.CURRENCY, value }));
        },
      ],
    ],
  },
  {
    event: events.SET_MOVING_AVERAGE,
    routes: [
      [TREND, 'setTrendMovingAverage'],
      [TREND_TABULAR, 'setTrendTabularMovingAverage'],
    ],
  },
  { event: events.SET_STEPS, routes: [[WEB_CLICK, 'setWebClickSteps']] },
  { event: events.SET_SCALE, routes: [[WEB_CLICK, 'setWebClickScaleFactor']] },
  { event: events.SET_BOOKING_PACE_WINDOW, routes: [[PACE, 'setPaceBookingPaceWindow']] },
  { event: events.SET_UTM_BREAKDOWN, routes: [[WEB_CLICK, 'setWebCLickUTMBreakdown']] },
  { event: events.SET_UTM_FOCUS_ON, routes: [[WEB_CLICK, 'setWebCLickUTMFocusOn']] },
  {
    event: events.SET_SEGMENT_BREAKDOWN_TOGGLE,
    routes: [
      [TREND, 'setTrendSegmentBreakdownToggle'],
      [TREND_TABULAR, 'setTrendTabularSegmentBreakdownToggle'],
      [PACE, 'setPaceSegmentBreakdownToggle'],
    ],
  },
  {
    event: events.SET_PERCENTAGE_TOGGLE,
    routes: [
      [TREND, 'setTrendPercentageChangeToggle'],
      [TREND_TABULAR, 'setTrendTabularPercentageChangeToggle'],
    ],
  },
  {
    event: events.SET_BREAKDOWN_TOGGLE,
    routes: [
      [TREND, 'setTrendBreakdownToggle'],
      [TREND_TABULAR, 'setTrendTabularBreakdownToggle'],
      [PACE, 'setPaceBreakdownToggle'],
    ],
  },
  {
    event: events.SET_ACTIVE_TAB,
    routes: [
      [TREND, 'setTrendActiveTab'],
      [PACE, 'setPaceActiveTab'],
    ],
  },
  {
    event: events.SET_SECONDARY_HOTEL_GROUP,
    routes: [
      [SUMMARY, 'setSummaryHotelGroups'],
      [WORKBOOK, 'setWorkbookHotelGroups'],
      [TREND, 'setTrendHotelGroups'],
      [TREND_TABULAR, 'setTrendTabularHotelGroups'],
      [WEB_CLICK, 'setWebClickHotelGroups'],
      [PACE, 'setPaceHotelGroups'],
    ],
  },
  {
    event: events.SET_FORECAST,
    routes: [
      [SUMMARY, 'setSummaryForecastToggle'],
      [TREND, 'setTrendForecastToggle'],
      [TREND_TABULAR, 'setTrendTabularForecastToggle'],
      [PACE, 'setPaceForecastToggle'],
    ],
  },
  {
    event: events.SET_SYMMETRIC_COMPARISON_COLLAPSE,
    routes: [
      [SUMMARY, 'setSummarySymmetricComparisonCollapse'],
      [WORKBOOK, 'setWorkbookSymmetricComparisonCollapse'],
      [TREND, 'setTrendSymmetricComparisonCollapse'],
      [TREND_TABULAR, 'setTrendTabularSymmetricComparisonCollapse'],
      [PACE, 'setPaceSymmetricComparisonCollapse'],
      [WEB_CLICK, 'setWebClickSymmetricComparisonCollapse'],
    ],
  },
  {
    event: events.SET_VISUAL_COLLAPSE,
    routes: [
      [SUMMARY, 'setSummaryVisualCollapse'],
      [TREND, 'setTrendVisualCollapse'],
      [WORKBOOK, 'setWorkbookVisualCollapse'],
      [PACE, 'setPaceVisualCollapse'],
      [WEB_CLICK, 'setWebClickVisualCollapse'],
      [REGION_MAP, 'setRegionalMapVisualCollapse'],
    ],
  },
  {
    event: events.SET_EXTENDING_FORECAST,
    routes: [[SUMMARY, 'setSummaryExtendingForecast']],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_PERCENTAGE,
    routes: [[PACE, 'setPacePercentage']],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_BOOKING_FORECAST,
    // no tab applies the booking forecast command, its value is received and cleared
    routes: [],
  },
  {
    event: events.SET_LAG_ALIGNED,
    routes: [[PACE, 'setPaceLagAligned']],
  },
  {
    event: events.SET_TIME_FRAME,
    routes: [[I_PREFER, 'setIPreferTimeFrame']],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_METRIC_SELECTION,
    routes: [[I_PREFER, 'setIPreferMetricSelection']],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_CUSTOM_AGGREGATION_SET_A,
    routes: [
      [TREND, 'setCustomAggregationAValue'],
      [TREND_TABULAR, 'setTrendTabularCustomAggregationAValue'],
    ],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_CUSTOM_AGGREGATION_SET_B,
    routes: [
      [TREND, 'setCustomAggregationBValue'],
      [TREND_TABULAR, 'setTrendTabularCustomAggregationBValue'],
    ],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_NORMALIZED_VIEW,
    routes: [
      [SUMMARY, 'setSummaryNormalizedView'],
      [WORKBOOK, 'setWorkbookNormalizedView'],
      [TREND, 'setTrendHotelNormalizedView'],
      [TREND_TABULAR, 'setTrendTabularHotelNormalizedView'],
      [WEB_CLICK, 'setWebClickNormalizedView'],
      [PACE, 'setPaceNormalizedView'],
    ],
  },
  {
    event: events.SET_FOCUS_ON,
    routes: [
      [REGION_MAP, 'setRegionalMapFocusOn'],
      [TREND, 'setTrendSegmentFocusOn'],
      [TREND_TABULAR, 'setTrendTabularSegmentFocusOn'],
      [WEB_CLICK, 'setWebClickFocusOn'],
      [I_PREFER, 'setIPreferFocusOn'],
      [PACE, 'setPaceFocusOn'],
    ],
  },
  {
    event: events.SET_BREAKDOWN,
    routes: [
      [SUMMARY, 'setSummaryBreakdown'],
      [REGION_MAP, 'setReginalMapBreakdown'],
      [TREND, 'setTrendSegmentBreakdown'],
      [TREND_TABULAR, 'setTrendTabularSegmentBreakdown'],
      [I_PREFER, 'setIPreferBreakdown'],
      [PACE, 'setPaceBreakdown'],
    ],
  },
  {
    event: events.SET_PACE_VARIABLE,
    routes: [[SUMMARY, 'setSummaryPaceVariable']],
  },
  {
    event: events.SET_PACE_HORIZON,
    routes: [
      [SUMMARY, 'setSummaryPaceHorizon'],
      [WEB_CLICK, 'setWebClickPaceHorizon'],
      [PACE, 'setPacePaceHorizon'],
    ],
  },
  {
    event: events.SET_COMPARE_WINDOW,
    routes: [
      [SUMMARY, 'setSummaryCompareWIndow'],
      [WORKBOOK, 'setWorkbookCompareWIndow'],
      [TREND, 'setTrendTimeCompareWIndow'],
      [TREND_TABULAR, 'setTrendTabularTimeCompareWIndow'],
      [WEB_CLICK, 'setWebClickCompareWIndow'],
      [I_PREFER, 'setIPreferCompareWIndow'],
      [PACE, 'setPaceCompareWIndow'],
    ],
  },
  {
    event: events.SET_CUSTOM_DATE_RANGE_VALUE,
    routes: [
      [SUMMARY, 'setSummaryCustomDateRange'],
      [WORKBOOK, 'setWorkbookCustomDateRange'],
      [TREND, 'setTrendTimeCustomDateRange'],
      [TREND_TABULAR, 'setTrendTabularCustomDateRange'],
      [WEB_CLICK, 'setWebClickCustomDateRange'],
      [I_PREFER, 'setIPreferCustomDateRange'],
    ],
  },
  {
    event: events.SET_CUMULATIVE_VIEW,
    routes: [
      [TREND, 'setTrendCumulativeView'],
      [TREND_TABULAR, 'setTrendTabularCumulativeView'],
      [
        WEB_CLICK,
        (value, dispatch) => dispatch(webClickActions.setWebClickCumulativeView(isOn(value))),
      ],
      [PACE, 'setPaceCumulativeView'],
    ],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_BOOKING_DATE,
    routes: [
      [SUMMARY, 'setSummaryCustomBooking'],
      [WORKBOOK, 'setWorkbookCustomBooking'],
      [REGION_MAP, 'setRelationalMapCustomBooking'],
      [TREND, 'setTrendCustomBooking'],
      [WEB_CLICK, 'setWebClickCustomBooking'],
      [I_PREFER, 'setWebIPreferCustomBooking'],
      [PACE, 'setPaceCustomBooking'],
      [TREND_TABULAR, 'setTrendTabularCustomBooking'],
    ],
  },
  {
    event: events.SYMMETRIC_COMPARISON,
    routes: [
      [SUMMARY, 'setSummarySymmetricComparison'],
      [TREND, 'setTrendSymmetricComparison'],
      [TREND_TABULAR, 'setTrendTabularSymmetricComparison'],
      [
        WEB_CLICK,
        (value, dispatch) =>
          dispatch(webClickActions.setWebClickSymmetricComparisonToggle(isOn(value))),
      ],
      [
        I_PREFER,
        (value, dispatch) =>
          dispatch(iPreferActions.setIPreferSymmetricComparisonToggle(isOn(value))),
      ],
      [WORKBOOK, 'setWorkbookSymmetricComparison'],
      [PACE, 'setPaceSymmetricComparison'],
    ],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    // tab setters of the alignment command take a boolean
    event: events.ALIGNMENT,
    value: isOn,
    routes: [
      [SUMMARY, 'setSummaryAlignment'],
      [WORKBOOK, 'setWorkbookAlignment'],
      [TREND, 'setTrendTimeAlignment'],
      [TREND_TABULAR, 'setTrendTabularTimeAlignment'],
      [WEB_CLICK, 'setWebClickAlignment'],
      [I_PREFER, 'setIPreferAlignment'],
      [PACE, 'setPaceAlignment'],
    ],
  },
  {
    event: events.WEEKLY_VIEW,
    routes: [
      [TREND, 'setTrendWeeklyView'],
      [TREND_TABULAR, 'setTrendTabularWeeklyView'],
      [
        WEB_CLICK,
        (value, dispatch) => dispatch(webClickActions.setWebClickWeeklyView(isOn(value))),
      ],
    ],
    clear: ROUTE_CLEAR.ROUTED,
  },
  {
    event: events.SET_TABULAR_VIEW,
    routes: [
      [TREND, 'setTrendTimeTabularView'],
      [PACE, 'setPaceTabularView'],
      [REGION_MAP, 'setRegionalMapTabularView'],
    ],
  },
  {
    event: events.SET_COMPARE_HOTELS,
    routes: [
      [SUMMARY, 'setSummaryHotelCompareHotels'],
      [WORKBOOK, 'setWorkbookHotelCompareHotels'],
      [TREND, 'setTrendHotelCompareHotels'],
      [TREND_TABULAR, 'setTrendTabularHotelCompareHotels'],
      [WEB_CLICK, 'setWebClickCompareHotels'],
      [PACE, 'setPaceCompareHotels'],
    ],
  },
];

export default COMMAND_ROUTES;
